import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, Detail, EmptyState, ErrorState, LoadingBlock, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { ApiError } from '../lib/api';
import {
  OUTCOME_LABEL,
  OUTCOME_TONE,
  PARTICIPANT_LABEL,
  RECORDING_LABEL,
  RECORDING_TONE,
  callClock,
  callsApi,
  endedText,
  isClientParty,
  type CallDetail,
  listSeparator,
  screenShares,
} from '../lib/calls';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatBytes, formatDateTime, formatTime } from '../lib/format';
import { useCallDetail } from './Calls';
import { CallAiAccessLog, CallAiSummaryCard } from '../components/CallAiSummary';

/**
 * One call (2026-10-29): who was asked, who answered, when each joined and left,
 * how it ended — and its recording. Whoever was really connected to the call
 * plays all of it, from its start; an administrator any. The player asks for a
 * link that lasts minutes, and asks again when it runs out, at the same moment.
 */
export function CallDetailPage() {
  const { id } = useParams();
  const { t, locale, localized } = useI18n();
  const { data: call, isLoading, error, refetch } = useCallDetail(id);
  if (isLoading) return <LoadingBlock />;
  if (error || !call) return <ErrorState error={error ?? new Error(t('calls.notFound'))} onRetry={() => void refetch()} />;

  const title =
    [call.caller, ...call.participants.map((p) => p.user).filter((u) => u.id !== call.caller?.id)]
      .filter(Boolean)
      .map((p) => localized(p!))
      .join(listSeparator(locale)) || t('calls.title');

  return (
    <>
      <div className="mb-4">
        <Link to="/calls" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('calls.title')}
        </Link>
      </div>
      <PageHeader
        title={title}
        subtitle={
          // A client's call from the portal says so (2026-11-01).
          (call.kind === 'CLIENT' ? `${t('calls.fromClient')} · ` : '') +
          (call.type === 'GROUP' ? t('calls.groupVoiceCall') : t('calls.voiceCall'))
        }
        action={<Badge tone={OUTCOME_TONE[call.outcome]}>{t(OUTCOME_LABEL[call.outcome])}</Badge>}
      />
      <div className="grid gap-5 lg:grid-cols-3">
        {/* min-w-0: a wide table (the access logs) scrolls inside its card instead of widening the page on a phone. */}
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <RecordingCard call={call} />
          <CallAiSummaryCard call={call} />
          <Card padded={false}>
            <div className="p-5 pb-0">
              <CardHeader title={t('calls.participants')} subtitle={t('calls.participantsHint')} />
            </div>
            <Table>
              <thead>
                <tr>
                  <Th>{t('calls.person')}</Th>
                  <Th>{t('calls.invitation')}</Th>
                  <Th>{t('calls.joined')}</Th>
                  <Th>{t('calls.left')}</Th>
                </tr>
              </thead>
              <tbody>
                {call.participants.map((p) => (
                  <Tr key={p.user.id}>
                    <Td>
                      <span className="text-[var(--text-primary)]">{localized(p.user)}</span>
                      {isClientParty(p.user) && (
                        <Badge tone="info" className="ms-2 align-middle">
                          {t('calls.clientBadge')}
                        </Badge>
                      )}
                      {p.isCaller && <span className="ms-2 text-caption text-muted">{t('calls.caller')}</span>}
                    </Td>
                    <Td>
                      <Badge tone={p.joinedAt ? 'success' : 'neutral'}>{t(PARTICIPANT_LABEL[p.status])}</Badge>
                    </Td>
                    <Td className="whitespace-nowrap text-muted tabular">{p.joinedAt ? formatTime(p.joinedAt, locale) : t('calls.neverJoined')}</Td>
                    <Td className="whitespace-nowrap text-muted tabular">
                      {p.leftAt ? formatTime(p.leftAt, locale) : '—'}
                      {p.leftReason === 'DROPPED' && <span className="ms-1 text-caption">({t('calls.dropped')})</span>}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <ScreensShared call={call} />
          {call.can.readAccessLog && <AccessLog callId={call.id} />}
          {call.can.readAccessLog && call.aiSummary?.eligible && <CallAiAccessLog callId={call.id} />}
        </div>
        <Card>
          <CardHeader title={t('calls.details')} />
          <dl>
            <Detail label={t('calls.status')}>{t(`calls.callStatus.${call.status}` as TranslationKey)}</Detail>
            <Detail label={t('calls.madeBy')}>
              {call.caller ? localized(call.caller) : t('calls.formerEmployee')}
              {isClientParty(call.caller) && (
                <Badge tone="info" className="ms-2 align-middle">
                  {t('calls.clientBadge')}
                </Badge>
              )}
            </Detail>
            {call.client && <Detail label={t('calls.client')}>{localized({ name: call.client.name, nameAr: call.client.nameAr })}</Detail>}
            <Detail label={t('calls.madeAt')}>{formatDateTime(call.createdAt, locale)}</Detail>
            <Detail label={t('calls.started')}>{call.startedAt ? formatDateTime(call.startedAt, locale) : '—'}</Detail>
            <Detail label={t('calls.ended')}>{call.endedAt ? formatDateTime(call.endedAt, locale) : '—'}</Detail>
            <Detail label={t('calls.duration')}>{call.durationSeconds !== null ? callClock(call.durationSeconds) : '—'}</Detail>
            {(call.status === 'FAILED' || call.endedReason === 'MAX_DURATION') && (
              <Detail label={t('calls.why')}>{t(endedText(call.failureCode ?? call.endedReason))}</Detail>
            )}
          </dl>
        </Card>
      </div>
    </>
  );
}

/**
 * Who shared a screen on the call, and when (2026-11-02) — from LiveKit's word, in the call's history. What it
 * showed is nowhere: the recording is the call's sound. Shown only for a call that had one.
 */
function ScreensShared({ call }: { call: CallDetail }) {
  const { t, locale, localized } = useI18n();
  const shares = screenShares(call.events);
  if (shares.length === 0) return null;
  return (
    <Card padded={false}>
      <div className="p-5 pb-0">
        <CardHeader title={t('calls.screensShared')} subtitle={t('calls.screensSharedHint')} />
      </div>
      <Table>
        <thead>
          <tr>
            <Th>{t('calls.person')}</Th>
            <Th>{t('calls.shareFrom')}</Th>
            <Th>{t('calls.shareUntil')}</Th>
          </tr>
        </thead>
        <tbody>
          {shares.map((s) => (
            <Tr key={`${s.user?.id ?? ''}-${s.from}`}>
              <Td className="text-[var(--text-primary)]">{s.user ? localized(s.user) : t('calls.formerEmployee')}</Td>
              <Td className="whitespace-nowrap text-muted tabular">{formatTime(s.from, locale)}</Td>
              <Td className="whitespace-nowrap text-muted tabular">{s.until ? formatTime(s.until, locale) : '—'}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function RecordingCard({ call }: { call: CallDetail }) {
  const { t, locale } = useI18n();
  const r = call.recording;
  const hint = (() => {
    if (r.playable) return r.expiresAt ? t('calls.keptUntil', { date: formatDateTime(r.expiresAt, locale) }) : t('calls.keptForever');
    switch (r.refusal) {
      case 'NOT_JOINED':
        return t('calls.refusal.NOT_JOINED');
      case 'INCOMPLETE':
        return t('calls.refusal.INCOMPLETE');
      case 'NOT_READY':
        return t('calls.refusal.NOT_READY');
      case 'EXPIRED':
        return r.deletedAt ? t('calls.refusal.EXPIRED_ON', { date: formatDateTime(r.deletedAt, locale) }) : t('calls.refusal.EXPIRED');
      case 'FAILED':
        return t('calls.refusal.FAILED');
      default:
        return t('calls.refusal.NO_RECORDING');
    }
  })();
  return (
    <Card>
      <CardHeader
        title={t('calls.recording')}
        subtitle={hint}
        action={r.shown !== 'NONE' && <Badge tone={RECORDING_TONE[r.shown]}>{t(RECORDING_LABEL[r.shown])}</Badge>}
      />
      {r.playable ? (
        <RecordingPlayer callId={call.id} partial={r.shown === 'PARTIAL'} />
      ) : (
        r.shown === 'NONE' && <EmptyState title={t('calls.recording.NONE')} hint={t('calls.noRecordingHint')} />
      )}
      {r.sizeBytes !== null && (
        <p className="mt-3 text-caption text-muted">
          {formatBytes(r.sizeBytes, locale)}
          {r.durationSeconds !== null && ` · ${callClock(r.durationSeconds)}`}
        </p>
      )}
    </Card>
  );
}

/**
 * The browser's own player — play, pause, seek, volume, length — on a link the
 * API signed for a few minutes. When the link runs out (a long listen, a pause),
 * a new one is asked for and the player goes on from where it was.
 */
function RecordingPlayer({ callId, partial }: { callId: string; partial: boolean }) {
  const { t } = useI18n();
  const audio = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const resumeAt = useRef<number | null>(null);

  const load = async (autoplay: boolean) => {
    setLoading(true);
    setProblem(null);
    try {
      const link = await callsApi.playback(callId);
      resumeAt.current = audio.current && src ? audio.current.currentTime : null;
      setSrc(link.url);
      setExpiresAt(Date.parse(link.expiresAt));
      if (autoplay) window.setTimeout(() => void audio.current?.play().catch(() => undefined), 0);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // A link about to run out is renewed before the next seek needs it.
  useEffect(() => {
    if (!src || !expiresAt) return;
    const renew = window.setTimeout(() => void load(!audio.current?.paused), Math.max(5_000, expiresAt - Date.now() - 30_000));
    return () => window.clearTimeout(renew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, expiresAt]);

  if (!src) {
    return (
      <div>
        {partial && <p className="mb-3 text-body-sm text-warning-fg dark:text-warning">{t('calls.partialWarning')}</p>}
        <Button variant="primary" loading={loading} onClick={() => void load(true)}>
          <Play className="h-4 w-4" aria-hidden />
          {t('calls.playRecording')}
        </Button>
        {problem && <p className="mt-2 text-body-sm text-error-fg dark:text-error-dark">{problem}</p>}
      </div>
    );
  }
  return (
    <div>
      {partial && <p className="mb-3 text-body-sm text-warning-fg dark:text-warning">{t('calls.partialWarning')}</p>}
      <audio
        ref={audio}
        src={src}
        controls
        preload="metadata"
        controlsList="nodownload noplaybackrate"
        className="w-full"
        onLoadedMetadata={() => {
          if (resumeAt.current !== null && audio.current) {
            audio.current.currentTime = resumeAt.current;
            resumeAt.current = null;
          }
        }}
        onError={() => {
          // An expired link answers 403: ask for a new one and go on from here.
          if (Date.now() > expiresAt - 30_000) void load(!audio.current?.paused);
          else setProblem(t('calls.cannotPlay'));
        }}
      />
      {problem && <p className="mt-2 text-body-sm text-error-fg dark:text-error-dark">{problem}</p>}
    </div>
  );
}

function AccessLog({ callId }: { callId: string }) {
  const { t, locale, localized } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ['calls', 'access', callId], queryFn: () => callsApi.access(callId) });
  return (
    <Card padded={false}>
      <div className="p-5 pb-0">
        <CardHeader title={t('calls.accessLog')} subtitle={t('calls.accessLogHint')} />
      </div>
      {isLoading ? (
        <LoadingBlock />
      ) : !data?.length ? (
        <p className="px-5 pb-5 text-body-sm text-muted">{t('calls.accessLogEmpty')}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('calls.when')}</Th>
              <Th>{t('calls.person')}</Th>
              <Th>{t('calls.accessResult')}</Th>
              <Th>{t('calls.accessFrom')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <Tr key={row.id}>
                <Td className="whitespace-nowrap text-muted">{formatDateTime(row.createdAt, locale)}</Td>
                <Td>{row.actor ? localized(row.actor) : t('calls.formerEmployee')}</Td>
                <Td>
                  {row.action === 'PLAYBACK_AUTHORIZED' ? (
                    <Badge tone="success">{t('calls.accessAllowed')}</Badge>
                  ) : (
                    <Badge tone="error">
                      {t('calls.accessRefused')}
                      {row.reason ? ` · ${t(`calls.refusalShort.${row.reason}` as TranslationKey)}` : ''}
                    </Badge>
                  )}
                </Td>
                <Td className="text-muted">
                  {row.channel === 'MOBILE' ? t('calls.viaPhone') : t('calls.viaWeb')}
                  {row.ipAddress && <span dir="ltr"> · {row.ipAddress}</span>}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
